import React, { useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  LinearProgress,
  Alert,
  Button,
  CardActions,
} from '@mui/material';
import {
  People,
  Article,
  Comment,
  TrendingUp,
  AdminPanelSettings,
  Settings,
  Person,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { authService } from '../services/authService';
import { UserCountWidget } from '../components/UserCountWidget';

export const HomePage: React.FC = () => {
  const { state, dispatch } = useApp();
  const navigate = useNavigate();

  // Load admin stats on component mount
  useEffect(() => {
    const loadAdminStats = async () => {
      if (state.user && authService.isAdmin(state.user)) {
        try {
          const response = await authService.getAdminStats();
          dispatch({
            type: 'SET_ADMIN_STATS',
            payload: response.data,
          });
        } catch (error) {
          console.error('Failed to load admin stats:', error);
        }
      }
    };

    loadAdminStats();
  }, [state.user, dispatch]);

  const stats = state.adminStats;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back, {state.user ? `${state.user.firstName} ${state.user.lastName}` : 'Admin'}
        </Typography>
        <Chip 
          label={state.user?.role?.toUpperCase() || 'ADMIN'} 
          color="primary" 
          size="small" 
          sx={{ mt: 1 }}
        />
      </Box>

      {!stats && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Loading admin statistics...
        </Alert>
      )}

      {!stats && <LinearProgress sx={{ mb: 3 }} />}

      <Grid container spacing={3}>
        {/* Statistics Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <People color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Users</Typography>
              </Box>
              <Typography variant="h3" color="primary">
                {stats?.totalUsers || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Registered users
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Article color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Posts</Typography>
              </Box>
              <Typography variant="h3" color="secondary">
                {stats?.totalPosts || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Published posts
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Comment color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Comments</Typography>
              </Box>
              <Typography variant="h3" color="success.main">
                {stats?.totalComments || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                User comments
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Active Users</Typography>
              </Box>
              <Typography variant="h3" color="warning.main">
                {stats?.activeUsers || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active this month
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* User Statistics Widget */}
        <Grid item xs={12}>
          <UserCountWidget />
        </Grid>

        {/* User Management Navigation */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AdminPanelSettings color="primary" sx={{ mr: 1 }} />
                <Typography variant="h5">User Management</Typography>
              </Box>
              <Typography variant="body1" paragraph>
                Manage all users, roles, and permissions. View, edit, disable, or delete user accounts.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • View all users in a searchable grid<br/>
                • Edit user information and roles<br/>
                • Enable/disable user accounts<br/>
                • Bulk actions for multiple users
              </Typography>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<People />}
                onClick={() => navigate('/admin/users')}
                fullWidth
              >
                Go to User Management
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Quick Actions
              </Typography>
              <Typography variant="body1" paragraph>
                Manage users, settings, and admin functions
              </Typography>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                variant="contained"
                startIcon={<People />}
                onClick={() => navigate('/admin/users')}
                sx={{ mr: 1 }}
              >
                User Management
              </Button>
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={() => navigate('/admin/settings')}
                sx={{ mr: 1 }}
              >
                Settings
              </Button>
              <Button
                variant="outlined"
                startIcon={<Person />}
                onClick={() => navigate('/admin/profile')}
              >
                Profile
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                System Status
              </Typography>
              <Typography variant="body1" paragraph>
                All systems operational
              </Typography>
              <Chip label="Online" color="success" size="small" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
